/**
 * Parent → Students lookup
 *
 * Resolves the logged-in parent's linked students via:
 *   auth.uid → users.id → parents.user_id → parents.id
 *   → student_parents.parent_id → students.id → users (names)
 *
 * Returned rows match the shape the parent dashboard DataTable expects
 * (attributes.first_name, etc.) so the existing DataTable code keeps working.
 *
 * The metric maths (progress %, current-period average, GPA band, last access)
 * lives in services/shared/studentMetrics.ts — the teacher dashboard runs the
 * SAME engine, so a child can never read one average on the parent screen and a
 * different one on the teacher screen. `computeChildMetrics` and the row type
 * are re-exported below so every existing `services/parent/students` import
 * keeps working unchanged.
 */
import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "../queryClient";
import { supabase } from "../supabase";
import { NOT_ARCHIVED_SUBJECT_ENROLMENT } from "../shared/archivedSubjectCourses";
import { useAuth } from "../../context/AuthContext";
import { getErrorMessage } from "@utils/helper";
import { computeChildMetrics, type StudentMetricsRow } from "../shared/studentMetrics";

// Re-export shim — see the module comment above.
export { computeChildMetrics } from "../shared/studentMetrics";

/** The parent roster row. Identical to the shared envelope; kept as a named
 *  alias because parent-side call sites import it under this name. */
export type ParentStudentRow = StudentMetricsRow;

// ---------------------------------------------------------------------------
// Parent's children's enrolled courses
// ---------------------------------------------------------------------------
// Joins: parents.user_id → parents → student_parents → course_enrollments
//        → courses → subjects
// Returned shape matches what CourseCardsTemplate expects (x.attributes.*).

export interface ParentCourseRow {
  id: string;
  student_id: string;
  course_title: string;
  attributes: {
    course_title: string;
    subject: { color: string | null; icon: string | null };
    school_year: { end_date: string | null } | null;
  };
}

// W2 dashboard-progress fix: the per (student, course) lesson tally that used
// to live here (`computeLessonTallies` — "every activity in the lesson
// answered") was a FOURTH, independent definition of "course progress",
// disagreeing with the Gradebook bar, the PT/teacher cards, AND the parent
// Dashboard's own (also broken) numerator. Removed. Progress is now merged
// onto these rows by the render site (parentWorkSpace/students/Courses.tsx)
// via useCourseProgressBatch(studentIds) — the same computeCourseProgress
// engine every other card now reads — using the `studentIds` this hook
// returns below.

const NO_COURSE_ROWS: ParentCourseRow[] = [];
const NO_STUDENT_IDS: string[] = [];

export async function fetchParentStudentCourses(profileId: string): Promise<{ rows: ParentCourseRow[]; studentIds: string[] }> {
  const { data: links, error: linkErr } = await supabase
    .from("student_parents")
    .select("student_id, parents!inner()")
    .eq("parents.user_id", profileId);

  if (linkErr) throw linkErr;
  const linkedStudentIds = (links ?? []).map((r) => r.student_id).filter(Boolean);

  if (linkedStudentIds.length === 0) return { rows: NO_COURSE_ROWS, studentIds: linkedStudentIds };

  const { data: enrollments, error: enrollErr } = await supabase
    .from("course_enrollments")
    .select(
      "id, student_id, courses(id, course_title, subjects(color, icon))",
    )
    .in("student_id", linkedStudentIds)
    .or(NOT_ARCHIVED_SUBJECT_ENROLMENT);

  if (enrollErr) throw enrollErr;

  const mapped: ParentCourseRow[] = (enrollments ?? [])
    .map((e): ParentCourseRow | null => {
      const course = Array.isArray(e.courses) ? e.courses[0] : e.courses;

      if (!course) return null;
      const subject = Array.isArray(course.subjects) ? course.subjects[0] : course.subjects;

      return {
        id: course.id,
        student_id: e.student_id,
        course_title: course.course_title ?? "Untitled",
        attributes: {
          course_title: course.course_title ?? "Untitled",
          subject: {
            color: subject?.color ?? null,
            icon: subject?.icon ?? null,
          },
          school_year: null, // TODO: join to cycles/school_years when needed
        },
      };
    })
    .filter((row): row is ParentCourseRow => row !== null);

  return { rows: mapped, studentIds: linkedStudentIds };
}

export function useGetParentStudentCourses() {
  const { profile } = useAuth();
  const profileId = profile?.id ?? null;

  const query = useQuery(
    {
      queryKey: ["parent", "studentCourses", profileId],
      queryFn: () => fetchParentStudentCourses(profileId as string),
      enabled: profileId != null,
      staleTime: 0,
      retry: false,
    },
    queryClient,
  );

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(async () => {
    if (profileId == null) return;
    await refetchQuery();
  }, [profileId, refetchQuery]);

  return {
    rows: query.data?.rows ?? NO_COURSE_ROWS,
    studentIds: query.data?.studentIds ?? NO_STUDENT_IDS,
    loading: query.isFetching,
    error: query.isError ? getErrorMessage(query.error, 'Failed to load courses') : null,
    refetch,
  };
}

// ---------------------------------------------------------------------------
// Flat lookup shape — for parent-side dropdowns and list pages that don't
// need the DataTable-flavored `attributes.*` envelope. The list pages used
// to call the generic `useGetStudentsApi({})` from services/shared/student
// which returned every student in the org (RLS is org-scoped only). Use
// this hook instead so parents only see their own children.
// ---------------------------------------------------------------------------

export interface ParentChildLookup {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  grade_level: string | null;
}

const NO_CHILDREN: ParentChildLookup[] = [];

export async function fetchMyChildrenLookup(profileId: string): Promise<ParentChildLookup[]> {
  const { data, error: sErr } = await supabase
    .from("student_parents")
    .select(
      "students(id, first_name, last_name, email, student_grade_levels(active, grade_level:grade_levels(grade_level))), parents!inner()",
    )
    .eq("parents.user_id", profileId);

  if (sErr) throw sErr;
  const mapped: ParentChildLookup[] = (data ?? [])
    .map((r) => (Array.isArray(r.students) ? r.students[0] : r.students))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))
    .map((s) => ({
      id: s.id,
      first_name: s.first_name ?? null,
      last_name: s.last_name ?? null,
      email: s.email ?? null,
      grade_level: s.student_grade_levels?.find((g: { active: boolean | null }) => g.active)?.grade_level?.grade_level ?? null,
    }));

  return mapped;
}

export function useGetMyChildrenLookup() {
  const { profile } = useAuth();
  const profileId = profile?.id ?? null;

  const query = useQuery(
    {
      queryKey: ["parent", "childrenLookup", profileId],
      queryFn: () => fetchMyChildrenLookup(profileId as string),
      enabled: profileId != null,
      staleTime: 0,
      retry: false,
    },
    queryClient,
  );

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(async () => {
    if (profileId == null) return;
    await refetchQuery();
  }, [profileId, refetchQuery]);

  return {
    rows: profileId == null ? NO_CHILDREN : (query.data ?? NO_CHILDREN),
    loading: query.isFetching,
    error: query.isError ? getErrorMessage(query.error, 'Failed to load children') : null,
    refetch,
  };
}

export async function fetchParentStudents(profileId: string): Promise<ParentStudentRow[]> {
  const { data, error: studentErr } = await supabase
    .from("student_parents")
    .select(
      "students(id, first_name, last_name, email, user_name, users!students_user_id_fkey(status)), parents!inner()",
    )
    .eq("parents.user_id", profileId);
  if (studentErr) throw studentErr;

  const students = (data ?? [])
    .map((row) => (Array.isArray(row.students) ? row.students[0] : row.students))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  const metrics = await computeChildMetrics(students.map((s) => s.id));

  return students.map((s) => {
    const m = metrics.get(s.id) ?? { progressCompleted: null, semesterAvg: null, periodAvgs: {}, gpa: null, lastAccess: null };
    const user = Array.isArray(s.users) ? s.users[0] : s.users;
    return {
      id: s.id,
      type: "student_progress_data",
      attributes: {
        first_name: s.first_name ?? "",
        last_name: s.last_name ?? "",
        email: s.email ?? null,
        user_name: s.user_name ?? null,
        status: user?.status ?? null,
        progress: { completed: m.progressCompleted },
        semsester_avg: m.semesterAvg,
        period_avgs: m.periodAvgs,
        gpa: m.gpa,
        last_access: m.lastAccess,
      },
    };
  });
}

export function useParentStudentsQuery() {
  const { profile } = useAuth();
  const profileId = profile?.id ?? null;

  return useQuery({
    queryKey: ["parent-students", profileId],
    queryFn: () => fetchParentStudents(profileId as string),
    enabled: profileId != null,
  });
}

const NO_PARENT_STUDENTS: ParentStudentRow[] = [];

export function useGetParentStudents() {
  const { profile } = useAuth();
  const profileId = profile?.id ?? null;

  const query = useQuery(
    {
      queryKey: ["parent-students", profileId],
      queryFn: () => fetchParentStudents(profileId as string),
      enabled: profileId != null,
      staleTime: 0,
      retry: false,
    },
    queryClient,
  );

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(async () => {
    if (profileId == null) return;
    await refetchQuery();
  }, [profileId, refetchQuery]);

  return {
    rows: query.data ?? NO_PARENT_STUDENTS,
    loading: query.isFetching,
    error: query.isError ? getErrorMessage(query.error, 'Failed to load students') : null,
    refetch,
  };
}
